import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Upload, X, Loader2, Receipt, ImageIcon } from "lucide-react";
import { Button } from "@tabipla/ui";
import { resizeImage } from "@tabipla/ui/lib/image-resize";
import { MAX_IMAGES_PER_ENTITY, type SessionImageDto } from "@tabipla/shared";

interface ReceiptUploaderProps {
  images: SessionImageDto[];
  onUpload: (file: File) => Promise<unknown>;
  onRemove: (imageId: string) => Promise<unknown>;
  isUploading: boolean;
  disabled?: boolean;
}

export function ReceiptUploader({
  images,
  onUpload,
  onRemove,
  isUploading,
  disabled = false,
}: ReceiptUploaderProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        return;
      }

      // Check image limit
      if (images.length >= MAX_IMAGES_PER_ENTITY) {
        return;
      }

      // Show preview
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      try {
        // Resize image before upload
        const resizedFile = await resizeImage(file);
        await onUpload(resizedFile);
      } finally {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
      }
    },
    [onUpload, images.length]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input
    e.target.value = "";
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = async (imageId: string) => {
    setRemovingId(imageId);
    try {
      await onRemove(imageId);
    } finally {
      setRemovingId(null);
    }
  };

  const hasImages = images.length > 0 || previewUrl;
  const isMaxReached = images.length >= MAX_IMAGES_PER_ENTITY;

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">{t("expenses.receipt", "レシート")}</label>

      {/* Existing images */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((image) => (
            <div key={image.id} className="group relative">
              <img
                src={image.url}
                alt="Receipt"
                className="h-20 w-20 rounded-lg border object-cover shadow-sm"
              />
              <button
                type="button"
                onClick={() => handleRemove(image.id)}
                disabled={removingId === image.id || disabled}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-white shadow-md transition-transform hover:scale-110 disabled:opacity-50"
              >
                {removingId === image.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Preview while uploading */}
      {previewUrl && (
        <div className="relative inline-block">
          <img
            src={previewUrl}
            alt="Uploading"
            className="h-20 w-20 rounded-lg border object-cover opacity-50"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        </div>
      )}

      {/* Upload area - hide when max reached */}
      {!isMaxReached && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative rounded-xl border-2 border-dashed transition-all ${
            dragActive
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50"
          } ${disabled || isUploading ? "pointer-events-none opacity-50" : ""}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled || isUploading}
          />

          <div className="flex flex-col items-center justify-center gap-3 p-6">
            {/* Icon */}
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-full transition-colors ${
                hasImages ? "bg-emerald-100 text-emerald-600" : "bg-muted text-muted-foreground"
              }`}
            >
              {isUploading ? (
                <Loader2 className="h-7 w-7 animate-spin" />
              ) : hasImages ? (
                <ImageIcon className="h-7 w-7" />
              ) : (
                <Receipt className="h-7 w-7" />
              )}
            </div>

            {/* Text */}
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">
                {isUploading
                  ? t("expenses.uploading", "アップロード中...")
                  : hasImages
                    ? t("expenses.addMoreReceipts", "追加する")
                    : t("expenses.addReceipt", "レシートを追加")}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground/70">
                {t("expenses.dragDropOrTap", "ドラッグ＆ドロップ または タップ")}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || isUploading}
                className="gap-1.5"
              >
                <Camera className="h-4 w-4" />
                {t("expenses.camera", "カメラ")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  // Remove capture attribute to allow gallery selection
                  if (fileInputRef.current) {
                    fileInputRef.current.removeAttribute("capture");
                    fileInputRef.current.click();
                    // Re-add capture for next time
                    setTimeout(() => {
                      fileInputRef.current?.setAttribute("capture", "environment");
                    }, 100);
                  }
                }}
                disabled={disabled || isUploading}
                className="gap-1.5"
              >
                <Upload className="h-4 w-4" />
                {t("expenses.gallery", "ギャラリー")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Max images info */}
      {isMaxReached && (
        <p className="text-center text-xs text-muted-foreground">
          {t("expenses.maxImagesReached", "最大{{max}}枚まで", { max: MAX_IMAGES_PER_ENTITY })}
        </p>
      )}
    </div>
  );
}
